import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Banknote,
  CheckCircle2,
  CircleDot,
  Clock,
  CreditCard,
  DollarSign,
  LineChart,
  MinusCircle,
  PackageOpen,
  Percent,
  Radio,
  Receipt,
  RefreshCw,
  ShoppingBasket,
  Smartphone,
  TimerReset,
  TrendingUp,
  Truck,
  User2,
  Utensils,
  Wallet,
  XCircle,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';

import { Spinner } from '@/components/ui/Spinner';
import { getCurrentShift, type CurrentShiftOrder } from '@/services/admin';
import { RemovalReason, WaiterCollectionsCard } from './shifts/WaiterShiftParts';
import { formatRub, orderStatusLabels, paymentMethodLabels } from '@/utils/format';

const REFRESH_MS = 5000;

const STATUS_COLORS: Record<string, string> = {
  NEW: 'bg-sky-50 text-sky-700 border-sky-200',
  IN_PROGRESS: 'bg-amber-50 text-amber-700 border-amber-200',
  READY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  COMPLETED: 'bg-slate-50 text-slate-600 border-slate-200',
  CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function CurrentShiftPage() {
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['admin/current-shift'],
    queryFn: getCurrentShift,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  if (isLoading) return <Spinner label="Подключаемся к кассе" />;
  if (!data) return null;

  if (!data.shift) {
    return (
      <div className="card p-10 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
          <TimerReset size={22} />
        </div>
        <h2 className="mt-4 text-lg font-semibold">Смена не открыта</h2>
        <p className="mt-1 text-sm text-slate-500">
          Данные появятся, как только кассир откроет смену на Flutter-кассе.
        </p>
        <button
          onClick={() => refetch()}
          className="mt-4 inline-flex items-center gap-2 text-sm text-brand-700 hover:text-brand-800"
        >
          <RefreshCw size={14} /> Обновить
        </button>
      </div>
    );
  }

  const { shift, totals, statuses, paymentMethods, sources, topDishes, hourly, activeOrders, recentOrders, payouts, expenses, itemChanges, waiterCollections } = data;
  const cashExpenses = totals?.cashExpenses ?? 0;
  const cashOut = (totals?.cashPayouts ?? 0) + cashExpenses;
  return (
    <div className="space-y-6">
      <HeaderBar
        shift={shift}
        lastUpdatedAt={dataUpdatedAt}
        isFetching={isFetching}
        onRefresh={() => refetch()}
      />

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <Kpi title="Выручка" value={formatRub(totals.revenue)} icon={<DollarSign size={20} />} tone="success" />
          <Kpi title="Заказов" value={totals.ordersCount.toString()} icon={<ShoppingBasket size={20} />} tone="brand" />
          <Kpi title="Средний чек" value={formatRub(totals.avgCheck)} icon={<LineChart size={20} />} tone="warning" />
          <Kpi
            title="Оплачено"
            value={totals.paidOrdersCount.toString()}
            hint={`${totals.ordersCount ? Math.round((totals.paidOrdersCount / totals.ordersCount) * 100) : 0}% от заказов`}
            icon={<Percent size={20} />}
            tone="slate"
          />
          <Kpi title="Отменено" value={totals.cancelledCount.toString()} icon={<XCircle size={20} />} tone="rose" />
          <Kpi
            title="В кассе (нал.)"
            value={formatRub(shift.expectedCash ?? totals.cashOnHand)}
            hint={cashOut ? `выдано из кассы −${formatRub(cashOut)}` : undefined}
            icon={<Banknote size={20} />}
            tone="violet"
          />
        </div>
      )}

      {totals?.grossProfit != null && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Kpi
              title="Себестоимость"
              value={formatRub(totals.cogs ?? 0)}
              hint={
                (totals.cogs ?? 0) === 0
                  ? 'Закупочные цены не заданы'
                  : `Фудкост ${totals.foodCostPercent ?? 0}%`
              }
              icon={<PackageOpen size={20} />}
              tone="warning"
            />
            <Kpi
              title="Валовая прибыль"
              value={formatRub(totals.grossProfit)}
              hint={`Маржа ${totals.marginPercent ?? 0}% · продажи смены ${formatRub(totals.salesRevenue ?? 0)}`}
              icon={<TrendingUp size={20} />}
              tone={totals.grossProfit < 0 ? 'rose' : 'success'}
            />
            <Kpi
              title="Чистая прибыль"
              value={formatRub(totals.netProfit ?? totals.grossProfit)}
              hint={
                cashExpenses + (totals.transferExpenses ?? 0) > 0
                  ? `минус расходы смены ${formatRub(cashExpenses + (totals.transferExpenses ?? 0))}`
                  : 'минус себестоимость и расходы'
              }
              icon={<Wallet size={20} />}
              tone={(totals.netProfit ?? 0) < 0 ? 'rose' : 'success'}
            />
          </div>
          {(totals.costCoverage ?? 0) < 95 && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Себестоимость известна у {totals.costCoverage ?? 0}% продаж смены — маржа
              считается только по ним. Закупочная цена задаётся в карточке блюда.
            </div>
          )}
        </div>
      )}

      {statuses && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {statuses.map((s) => (
            <StatusChip key={s.status} status={s.status} count={s.count} revenue={s.revenue} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5 lg:col-span-2">
          <SectionHeader
            title="Активные заказы"
            subtitle="NEW · IN_PROGRESS · READY"
            right={
              <span className="text-xs text-slate-400">
                Всего: {activeOrders?.length ?? 0}
              </span>
            }
          />
          <OrdersTable orders={activeOrders ?? []} emptyHint="Сейчас нет активных заказов." />
        </div>

        <div className="card p-5">
          <SectionHeader title="Источники" subtitle="Касса vs. приложение" />
          <div className="space-y-2">
            {(sources ?? []).map((s) => {
              const total = (sources ?? []).reduce((sum, x) => sum + x.revenue, 0);
              const share = total > 0 ? Math.round((s.revenue / total) * 100) : 0;
              return (
                <div key={s.source} className="p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 font-medium">
                      {s.source === 'CASH' ? <CircleDot size={14} /> : <Smartphone size={14} />}
                      {s.source === 'CASH' ? 'Касса' : 'Приложение'}
                    </span>
                    <span className="text-slate-400">{share}%</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-lg font-semibold">{formatRub(s.revenue)}</span>
                    <span className="text-xs text-slate-500">{s.count} шт</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${s.source === 'CASH' ? 'bg-brand-500' : 'bg-emerald-500'}`}
                      style={{ width: `${share}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <SectionHeader title="Оплаты по способам" subtitle="Только PAID в этой смене" />
          <div className="space-y-2">
            {(paymentMethods ?? []).filter((p) => p.amount > 0).length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">Пока ничего не оплачено.</div>
            ) : (
              (paymentMethods ?? [])
                .filter((p) => p.amount > 0)
                .map((p) => (
                  <div key={p.method} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="flex items-center gap-2">
                      {p.method === 'CASH' ? (
                        <Banknote size={14} className="text-emerald-500" />
                      ) : (
                        <CreditCard size={14} className="text-brand-500" />
                      )}
                      {paymentMethodLabels[p.method] ?? p.method}
                    </span>
                    <span className="flex gap-4">
                      <span className="text-slate-400">{p.count} шт</span>
                      <span className="font-semibold">{formatRub(p.amount)}</span>
                    </span>
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="card p-5">
          <SectionHeader title="Активность по часам" subtitle="Количество заказов" />
          <div className="h-56">
            {hourly && hourly.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="hour" tickFormatter={(v) => `${v}:00`} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v: number, _n, p) => [`${v} заказов · ${formatRub(p.payload.revenue)}`, `${p.payload.hour}:00`]}
                  />
                  <Bar dataKey="count" fill="#4f62e0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-sm text-slate-400 text-center pt-16">Ещё нет заказов.</div>
            )}
          </div>
        </div>
      </div>

      {payouts && payouts.length > 0 && (
        <div className="card p-5">
          <SectionHeader
            title="Выплаты из кассы"
            subtitle="Наличные, выданные из ящика — за поставки"
            right={
              <span className="text-sm font-semibold text-rose-600">
                −{formatRub(totals?.cashPayouts ?? payouts.reduce((s, p) => s + p.paidAmount, 0))}
              </span>
            }
          />
          <div className="divide-y divide-slate-100">
            {payouts.map((p) => (
              <div key={p.id} className="py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium flex items-center gap-2 min-w-0">
                    <Truck size={14} className="text-slate-400 shrink-0" />
                    <span className="truncate">{p.supplierName ?? 'Поставка'}</span>
                    <span className="text-xs text-slate-400 shrink-0">{formatClock(p.createdAt)}</span>
                  </span>
                  <span className="font-semibold text-rose-600 shrink-0">−{formatRub(p.paidAmount)}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {p.items.map((it) => `${it.name} ${it.quantity} ${it.unit}`).join(' · ') || '—'}
                  {p.comment ? <span className="text-slate-400"> · {p.comment}</span> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {expenses && expenses.length > 0 && (
        <div className="card p-5">
          <SectionHeader
            title="Расходы смены"
            subtitle="Траты, проведённые через кассу этой смены"
            right={
              <Link to="/expenses" className="text-xs text-brand-600 hover:underline">
                Все расходы
              </Link>
            }
          />
          <div className="divide-y divide-slate-100">
            {expenses.map((e) => (
              <div key={e.id} className="py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium flex items-center gap-2 min-w-0">
                    <Receipt size={14} className="text-slate-400 shrink-0" />
                    <span className="truncate">{e.category?.name ?? 'Без статьи'}</span>
                    <span className="text-xs text-slate-400 shrink-0">{formatClock(e.createdAt)}</span>
                  </span>
                  <span className="font-semibold text-rose-600 shrink-0">−{formatRub(e.amount)}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {e.method === 'KASSA_CASH' ? 'Наличными из кассы' : 'Переводом с кассы'}
                  {e.comment ? <span className="text-slate-400"> · {e.comment}</span> : null}
                  {e.user?.name ? <span className="text-slate-400"> · {e.user.name}</span> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <WaiterCollectionsCard rows={waiterCollections} />

      {itemChanges && itemChanges.length > 0 && (
        <div className="card p-5">
          <SectionHeader
            title="Снятые позиции"
            subtitle="Кассир или официант уменьшил количество или убрал позицию из заказа"
            right={
              <span className="text-sm font-semibold text-amber-700">
                {totals?.removedItemsQty ?? itemChanges.length} шт ·{' '}
                {formatRub(totals?.removedItemsAmount ?? 0)}
              </span>
            }
          />
          <div className="divide-y divide-slate-100">
            {itemChanges.map((c) => (
              <div key={c.id} className="py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium flex items-center gap-2 min-w-0">
                    <MinusCircle size={14} className="text-amber-500 shrink-0" />
                    <span className="truncate">{c.dishName}</span>
                    <span className="text-xs text-slate-400 shrink-0">{formatClock(c.createdAt)}</span>
                  </span>
                  <span className="font-semibold text-amber-700 shrink-0 tabular-nums">
                    {c.qtyBefore} → {c.qtyAfter} · −{formatRub(c.amount)}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {c.orderNumber != null ? `Заказ #${c.orderNumber}` : 'Заказ удалён'}
                  {c.tableNumber != null ? ` · стол ${c.tableNumber}` : ''}
                  {c.actorName ? (
                    <span className="text-slate-400"> · официант {c.actorName}</span>
                  ) : (
                    <>
                      {c.cashierName ? <span className="text-slate-400"> · {c.cashierName}</span> : null}
                      {c.deviceName ? <span className="text-slate-400"> · {c.deviceName}</span> : null}
                    </>
                  )}
                </div>
                <div className="text-xs">
                  <RemovalReason change={c} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <SectionHeader title="Топ-5 блюд" subtitle="За текущую смену" />
          {!topDishes || topDishes.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Продаж пока нет.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {topDishes.map((d, idx) => (
                <div key={d.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 text-xs flex items-center justify-center font-semibold shrink-0">
                      {idx + 1}
                    </span>
                    <Utensils size={14} className="text-slate-400 shrink-0" />
                    <span className="truncate">{d.name}</span>
                  </span>
                  <span className="flex gap-4 shrink-0 items-baseline">
                    <span className="text-slate-400">{d.quantity} шт</span>
                    {(d.cost ?? 0) > 0 && (
                      <span className="text-xs text-emerald-600">
                        прибыль {formatRub(d.profit ?? 0)}
                      </span>
                    )}
                    <span className="font-medium">{formatRub(d.total)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card p-5">
          <SectionHeader title="Последние заказы" subtitle="15 последних за смену" />
          <OrdersTable orders={(recentOrders ?? []).slice(0, 10)} emptyHint="Заказов ещё нет." compact />
        </div>
      </div>
    </div>
  );
}

// ─── UI ───────────────────────────────────────────────────────

function HeaderBar({
  shift,
  lastUpdatedAt,
  isFetching,
  onRefresh,
}: {
  shift: NonNullable<import('@/services/admin').CurrentShiftPayload['shift']>;
  lastUpdatedAt: number;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  const since = useMemo(() => formatClock(shift.openedAt), [shift.openedAt]);
  const duration = formatDuration(shift.durationMin);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  const updatedAgo = lastUpdatedAt > 0 ? Math.max(0, Math.round((Date.now() - lastUpdatedAt) / 1000)) : 0;

  return (
    <header className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="relative">
          <span className="h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
            <Radio size={20} />
          </span>
          <span className="absolute -top-0.5 -right-0.5 inline-flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
          </span>
        </div>
        <div>
          <h1 className="text-lg sm:text-2xl font-semibold leading-tight">
            <span className="hidden sm:inline">Текущая касса · </span>смена #{shift.number}
          </h1>
          <div className="text-sm text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Clock size={14} /> открыта в {since} · {duration}
            </span>
            {(shift.cashierName || shift.cashier) && (
              <span className="inline-flex items-center gap-1">
                <User2 size={14} />{' '}
                {shift.cashierName ??
                  shift.cashier?.name ??
                  shift.cashier?.phone ??
                  shift.cashier?.email ??
                  'Кассир'}
                {shift.cashierName && shift.cashier?.name && shift.cashier.name !== shift.cashierName ? (
                  <span className="text-slate-400">· {shift.cashier.name}</span>
                ) : null}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Banknote size={14} /> на открытие: {formatRub(shift.cashOnOpen)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>
          обновлено {updatedAgo}с назад · авто каждые {REFRESH_MS / 1000}с
        </span>
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50"
        >
          <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
          Обновить
        </button>
      </div>
    </header>
  );
}

function Kpi({
  title,
  value,
  icon,
  tone,
  hint,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  tone: 'brand' | 'success' | 'warning' | 'slate' | 'rose' | 'violet';
  hint?: string;
}) {
  const iconBg: Record<typeof tone, string> = {
    brand: 'bg-brand-500 text-white',
    success: 'bg-emerald-500 text-white',
    warning: 'bg-amber-400 text-white',
    slate: 'bg-slate-500 text-white',
    rose: 'bg-rose-500 text-white',
    violet: 'bg-violet-500 text-white',
  };
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">{title}</div>
          <div className="text-2xl font-semibold mt-1">{value}</div>
          {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
        </div>
        <div className={`p-2 rounded-lg ${iconBg[tone]}`}>{icon}</div>
      </div>
    </div>
  );
}

function StatusChip({ status, count, revenue }: { status: string; count: number; revenue: number }) {
  const color = STATUS_COLORS[status] ?? 'bg-slate-50 text-slate-600 border-slate-200';
  const Icon = status === 'COMPLETED' ? CheckCircle2 : status === 'CANCELLED' ? XCircle : CircleDot;
  return (
    <div className={`rounded-lg border ${color} px-3 py-2.5`}>
      <div className="flex items-center gap-2 text-xs font-medium">
        <Icon size={14} />
        {orderStatusLabels[status] ?? status}
      </div>
      <div className="mt-1 flex items-baseline justify-between">
        <span className="text-lg font-semibold">{count}</span>
        <span className="text-xs opacity-70">{formatRub(revenue)}</span>
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-2">
      <div>
        <h3 className="font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function OrdersTable({
  orders,
  emptyHint,
  compact,
}: {
  orders: CurrentShiftOrder[];
  emptyHint: string;
  compact?: boolean;
}) {
  if (orders.length === 0) {
    return <div className="text-sm text-slate-400 py-6 text-center">{emptyHint}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-100">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="text-left px-3 py-2 font-medium">№</th>
            <th className="text-left px-3 py-2 font-medium">Статус</th>
            <th className="text-left px-3 py-2 font-medium">Источник</th>
            {!compact && <th className="text-left px-3 py-2 font-medium">Стол</th>}
            {!compact && <th className="text-left px-3 py-2 font-medium">Клиент</th>}
            <th className="text-right px-3 py-2 font-medium">Сумма</th>
            <th className="text-right px-3 py-2 font-medium">Создан</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {orders.map((o) => {
            const cls = STATUS_COLORS[o.status] ?? 'bg-slate-50 text-slate-600 border-slate-200';
            return (
              <tr key={o.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">#{o.number}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${cls}`}>
                    {orderStatusLabels[o.status] ?? o.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-600">
                  <span className="inline-flex items-center gap-1">
                    {o.source === 'APP' ? <Smartphone size={12} /> : <CircleDot size={12} />}
                    {o.source === 'APP' ? 'Приложение' : 'Касса'}
                  </span>
                </td>
                {!compact && (
                  <td className="px-3 py-2 text-slate-600">
                    {o.table ? `Стол ${o.table.number}` : <span className="text-slate-300">—</span>}
                  </td>
                )}
                {!compact && (
                  <td className="px-3 py-2 text-slate-600">
                    {o.client?.name ?? o.client?.phone ?? <span className="text-slate-300">—</span>}
                  </td>
                )}
                <td className="px-3 py-2 text-right font-medium">{formatRub(o.total)}</td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">{formatClock(o.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────

function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} мин`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} ч` : `${h} ч ${m} мин`;
}
